import { createContext, useState, useContext, useEffect, useCallback } from "react";
import API from "../lib/api";
import { isImpersonating, restoreAdminSession } from "../lib/impersonation";
import { rememberPortalSlug, forgetPortalSlug } from "../lib/tenantPortal";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await API.get("/auth/me");
      if (!data.impersonating && isImpersonating()) restoreAdminSession(); // support session expired/ended
      setUser(data);
    } catch {
      if (isImpersonating()) {
        // Support token expired — fall back to the platform admin's own session
        restoreAdminSession();
        try {
          const { data } = await API.get("/auth/me");
          setUser(data);
          return;
        } catch {}
      }
      setUser(false);
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password, tenantSlug) => {
    const { data } = await API.post("/auth/login", tenantSlug ? { email, password, tenant_slug: tenantSlug } : { email, password });
    if (data.requires_mfa) {
      return { requires_mfa: true, email: data.email, mfa_token: data.mfa_token };
    }
    if (tenantSlug && !data.is_platform_admin) rememberPortalSlug(tenantSlug); else forgetPortalSlug();
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data);
    hydrate();
    return data;
  };

  // Login payloads are lean; pull the full profile (features, billing alert, tenant name) right after so banners/gates are right on first render
  const hydrate = () => {
    API.get("/auth/me").then(({ data: me }) => setUser((prev) => (prev ? { ...prev, ...me } : prev))).catch(() => {});
  };

  // Second sign-in step: the short-lived mfa_token from /auth/login proves the password was just checked
  const verifyMfa = async (mfaToken, otpCode, backupCode) => {
    const { data } = await API.post("/mfa/verify-login", { mfa_token: mfaToken, otp_code: otpCode || "", backup_code: backupCode || "" });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data);
    hydrate();
    return data;
  };

  const register = async (email, password, name, role) => {
    const { data } = await API.post("/auth/register", { email, password, name, role });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    // engineers sharing a live position: wipe it server-side before the session goes (the provider listens for this)
    try { window.dispatchEvent(new CustomEvent("fixli:before-logout")); await new Promise((r) => setTimeout(r, 150)); } catch {}
    try { await API.post("/auth/logout"); } catch {}
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    try { sessionStorage.removeItem("portal.focusClient"); } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, register, logout, checkAuth, formatApiError: (d) => {
      if (d == null) return "Something went wrong.";
      if (typeof d === "string") return d;
      if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
      return String(d);
    }}}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
